/**
 * ContentCache - IndexedDB-based content caching for offline capability
 *
 * Stores:
 * - API responses (ads, streams, l-bar content)
 * - Media blobs (videos, images)
 * - Stream queue state
 *
 * Designed to work with both React (ES module) and vanilla JS (window global)
 */

class ContentCache {
  constructor() {
    this.dbName = 'TrillboardsContentDB';
    this.dbVersion = 2; // Bump version for new schema with priority
    this.db = null;
    this.isInitialized = false;
    this.initPromise = null;

    // Default TTLs by priority (in ms)
    this.defaultTTL = {
      apiResponse: 60 * 60 * 1000,    // 1 hour
      mediaBlob: 24 * 60 * 60 * 1000, // 24 hours (default, overridden by priority)
      streamQueue: 30 * 60 * 1000     // 30 minutes
    };

    // Priority-based TTLs for media
    this.mediaTTLByPriority = {
      1: 48 * 60 * 60 * 1000,   // Paid ads: 48 hours
      1.5: 24 * 60 * 60 * 1000, // VAST/IMA ads: 24 hours
      2: 24 * 60 * 60 * 1000,   // Self-promo: 24 hours
      3: 12 * 60 * 60 * 1000    // Default streams: 12 hours
    };

    // Priority weights for eviction (higher = harder to evict)
    // Priority 1.5 is VAST/IMA ads (between paid and self-promo)
    this.priorityWeights = {
      1: 4,   // Paid ads - kept longest
      1.5: 3, // VAST/IMA ads - high priority
      2: 2,   // Self-promo
      3: 1    // Default streams
    };

    // VAST-specific TTL (24 hours — most creatives rotate daily, not hourly.
    // 6h forced 4 re-downloads/day of the same creative; 24h reduces to 1.)
    this.vastTTL = 24 * 60 * 60 * 1000;

    // Device class for adaptive storage limits
    this.deviceClass = this._detectDeviceClass();

    // Storage thresholds (in bytes)
    this.storageThresholds = {
      evictionTrigger: 0.8,    // Start eviction at 80% capacity
      targetFree: 0.3,         // Evict until 30% free
      maxFileSize: {
        high: 100 * 1024 * 1024,    // 100MB for devices with >= 512MB quota
        medium: 30 * 1024 * 1024,   // 30MB for devices with 256-512MB quota
        low: 5 * 1024 * 1024        // 5MB for devices with < 256MB quota
      }
    };

    // Cache metrics
    this.metrics = {
      hits: 0,
      misses: 0,
      bytesServedFromCache: 0,
      bytesFetchedFromNetwork: 0,
      evictions: 0,
      errors: 0
    };

    this.logPrefix = '[ContentCache]';

    // Track active blob URLs to prevent memory leaks
    // Map of blobUrl -> { sourceUrl, createdAt, size, refCount }
    this.activeBlobUrls = new Map();
    this.maxActiveBlobUrls = 50; // Maximum concurrent blob URLs
    this.blobUrlEvictionCount = 10; // Number of oldest blob URLs to revoke when limit reached

    // URL reuse mechanism - prevents creating duplicate blob URLs for the same source
    // Map of sourceUrl -> blobUrl
    this.sourceUrlToBlobUrl = new Map();

    // Deferred revocation - blob URLs to revoke after timeout (allows reuse)
    // Map of blobUrl -> timeoutId
    this.pendingRevocations = new Map();
    this.blobUrlRevokeDelayMs = 30000; // 30 seconds delay before revoking

    // Request deduplication - prevents concurrent fetches for the same URL
    // Map of url -> Promise<Response>
    this._inflight = new Map();

    // HEAD validation throttle - skip if validated within this window
    this._validationThrottleMs = 10 * 60 * 1000; // 10 minutes

    // VAST blob URL map: cacheKey (e.g. 'vast:709996466052') -> blobUrl
    // Provides synchronous lookups for ad video src replacement.
    // Rebuilt from IndexedDB on init, updated on cache/evict.
    this._vastBlobMap = new Map();

    // VAST inflight dedup: cacheKey -> Promise (prevents double-fetch of same creative)
    // Keyed by cacheKey (not URL) since googlevideo generates unique URLs per request.
    this._vastInflight = new Map();
  }

  /**
   * Create a tracked blob URL that can be properly cleaned up
   * Implements URL reuse to prevent creating duplicate blob URLs for the same source
   * @param {Blob} blob - The blob to create a URL for
   * @param {string} sourceUrl - The original source URL (for tracking)
   * @param {number} size - Optional size in bytes
   * @returns {string} The blob URL
   */
  createTrackedBlobUrl(blob, sourceUrl = '', size = 0) {
    // Check if we already have a blob URL for this source (URL reuse)
    if (sourceUrl && this.sourceUrlToBlobUrl.has(sourceUrl)) {
      const existingBlobUrl = this.sourceUrlToBlobUrl.get(sourceUrl);
      const info = this.activeBlobUrls.get(existingBlobUrl);
      if (info) {
        // Cancel any pending revocation
        if (this.pendingRevocations.has(existingBlobUrl)) {
          clearTimeout(this.pendingRevocations.get(existingBlobUrl));
          this.pendingRevocations.delete(existingBlobUrl);
        }
        // MEMORY FIX: Do NOT increment refCount on reuse.
        // refCount was growing monotonically (2.6/min) because revokeTrackedBlobUrl
        // was never called, permanently pinning blobs in memory.
        // Just update lastAccessed for LRU eviction. refCount stays at 1.
        info.lastAccessed = Date.now();
        return existingBlobUrl;
      }
      // Clean up stale mapping
      this.sourceUrlToBlobUrl.delete(sourceUrl);
    }

    // Check if we need to evict old blob URLs
    if (this.activeBlobUrls.size >= this.maxActiveBlobUrls) {
      this._evictOldestBlobUrls();
    }

    const blobUrl = URL.createObjectURL(blob);
    this.activeBlobUrls.set(blobUrl, {
      sourceUrl,
      createdAt: Date.now(),
      lastAccessed: Date.now(),
      size: size || blob.size || 0,
      refCount: 1
    });

    // Track source URL to blob URL mapping for reuse
    if (sourceUrl) {
      this.sourceUrlToBlobUrl.set(sourceUrl, blobUrl);
    }

    return blobUrl;
  }

  /**
   * Release a tracked blob URL (decrement reference count)
   * The URL is not immediately revoked - it's scheduled for deferred revocation
   * to allow reuse if the same content is requested again
   * @param {string} blobUrl - The blob URL to release
   */
  revokeTrackedBlobUrl(blobUrl) {
    const info = this.activeBlobUrls.get(blobUrl);
    if (!info) return;

    // Decrement reference count
    info.refCount = Math.max(0, (info.refCount || 1) - 1);

    // If still referenced, don't revoke
    if (info.refCount > 0) return;

    // Schedule deferred revocation (allows reuse within 30s)
    if (!this.pendingRevocations.has(blobUrl)) {
      const timeoutId = setTimeout(() => {
        this._forceRevokeBlobUrl(blobUrl);
      }, this.blobUrlRevokeDelayMs);
      this.pendingRevocations.set(blobUrl, timeoutId);
    }
  }

  /**
   * Forcefully revoke a blob URL (called after deferred timeout or during cleanup)
   * @param {string} blobUrl - The blob URL to revoke
   * @private
   */
  _forceRevokeBlobUrl(blobUrl) {
    // Clear any pending timeout
    if (this.pendingRevocations.has(blobUrl)) {
      clearTimeout(this.pendingRevocations.get(blobUrl));
      this.pendingRevocations.delete(blobUrl);
    }

    const info = this.activeBlobUrls.get(blobUrl);
    if (info) {
      // Remove source URL mapping
      if (info.sourceUrl) {
        this.sourceUrlToBlobUrl.delete(info.sourceUrl);
      }
      // Revoke the blob URL
      try {
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        // Ignore errors revoking blob URLs
      }
      this.activeBlobUrls.delete(blobUrl);
    }
  }

  /**
   * Revoke all active blob URLs (for cleanup)
   */
  revokeAllBlobUrls() {
    // Cancel all pending revocations
    this.pendingRevocations.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.pendingRevocations.clear();

    const count = this.activeBlobUrls.size;
    this.activeBlobUrls.forEach((_, blobUrl) => {
      try {
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        // Ignore errors
      }
    });
    this.activeBlobUrls.clear();
    this.sourceUrlToBlobUrl.clear();
    if (count > 0) {
      console.log(`${this.logPrefix} Revoked ${count} blob URLs`);
    }
  }

  /**
   * Clean up stale blob URLs that haven't been accessed recently.
   * Safety net to prevent long-running screens from accumulating pinned blobs.
   * Called periodically from the overlay's 30-minute memory telemetry interval.
   * @param {number} maxAgeMs - Maximum age in ms before a blob is considered stale (default: 30 minutes)
   * @returns {number} Number of stale blob URLs revoked
   */
  cleanupStaleBlobUrls(maxAgeMs = 30 * 60 * 1000) {
    const now = Date.now();
    const stale = [];
    this.activeBlobUrls.forEach((info, blobUrl) => {
      if (now - info.lastAccessed > maxAgeMs && info.refCount <= 1) {
        stale.push(blobUrl);
      }
    });
    stale.forEach(blobUrl => this._forceRevokeBlobUrl(blobUrl));
    if (stale.length > 0) {
      console.log(`${this.logPrefix} Cleaned up ${stale.length} stale blob URLs (older than ${Math.round(maxAgeMs / 60000)}min)`);
    }
    return stale.length;
  }

  /**
   * Check if a blob URL is still actively tracked (not revoked)
   * @param {string} blobUrl - The blob URL to check
   * @returns {boolean} true if the blob URL is still active
   */
  isBlobUrlActive(blobUrl) {
    if (!blobUrl || !blobUrl.startsWith('blob:')) return false;
    return this.activeBlobUrls.has(blobUrl);
  }

  /**
   * Evict oldest blob URLs when limit is reached
   * Prioritizes evicting URLs with refCount 0 and pending revocation
   * @private
   */
  _evictOldestBlobUrls() {
    // Sort by: pending revocation first, then refCount (lowest first), then createdAt (oldest first)
    const entries = Array.from(this.activeBlobUrls.entries());
    entries.sort((a, b) => {
      const aPending = this.pendingRevocations.has(a[0]) ? 0 : 1;
      const bPending = this.pendingRevocations.has(b[0]) ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;

      const aRef = a[1].refCount || 1;
      const bRef = b[1].refCount || 1;
      if (aRef !== bRef) return aRef - bRef;

      return a[1].createdAt - b[1].createdAt;
    });

    const toEvict = entries.slice(0, this.blobUrlEvictionCount);
    let freedBytes = 0;

    toEvict.forEach(([blobUrl, info]) => {
      // Cancel any pending revocation timeout
      if (this.pendingRevocations.has(blobUrl)) {
        clearTimeout(this.pendingRevocations.get(blobUrl));
        this.pendingRevocations.delete(blobUrl);
      }
      // Remove source URL mapping
      if (info.sourceUrl) {
        this.sourceUrlToBlobUrl.delete(info.sourceUrl);
      }
      try {
        URL.revokeObjectURL(blobUrl);
        freedBytes += info.size;
      } catch (e) {
        // Ignore errors
      }
      this.activeBlobUrls.delete(blobUrl);
    });

    console.log(`${this.logPrefix} Evicted ${toEvict.length} old blob URLs (freed ~${this._formatBytes(freedBytes)})`);
  }

  /**
   * Get count of active blob URLs (for monitoring)
   * @returns {number}
   */
  getActiveBlobUrlCount() {
    return this.activeBlobUrls.size;
  }

  /**
   * Destroy the cache and clean up all resources
   */
  destroy() {
    // Revoke all active blob URLs
    this.revokeAllBlobUrls();

    // Close the database connection
    if (this.db) {
      this.db.close();
      this.db = null;
    }

    this.isInitialized = false;
    this.initPromise = null;
    console.log(`${this.logPrefix} Cache destroyed and resources cleaned up`);
  }

  /**
   * Initialize the IndexedDB database
   */
  async init() {
    // Prevent multiple initializations
    if (this.initPromise) {
      return this.initPromise;
    }

    if (this.isInitialized) {
      return Promise.resolve(this.db);
    }

    this.initPromise = new Promise((resolve, reject) => {
      // Check for IndexedDB support
      if (!('indexedDB' in window)) {
        console.warn(`${this.logPrefix} IndexedDB not supported`);
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error(`${this.logPrefix} Failed to open database:`, request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        console.log(`${this.logPrefix} Database initialized`);
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        const oldVersion = event.oldVersion;

        // Store for API responses
        if (!db.objectStoreNames.contains('apiResponses')) {
          const apiStore = db.createObjectStore('apiResponses', { keyPath: 'endpoint' });
          apiStore.createIndex('timestamp', 'timestamp');
          apiStore.createIndex('screenId', 'screenId');
        }

        // Store for media blobs - v2 adds priority-based caching
        if (!db.objectStoreNames.contains('mediaBlobs')) {
          const mediaStore = db.createObjectStore('mediaBlobs', { keyPath: 'url' });
          mediaStore.createIndex('timestamp', 'timestamp');
          mediaStore.createIndex('type', 'type');
          mediaStore.createIndex('priority', 'priority');
          mediaStore.createIndex('lastPlayed', 'lastPlayed');
          mediaStore.createIndex('etag', 'etag');
        } else if (oldVersion < 2) {
          // Migrate existing store to add new indexes
          const tx = event.target.transaction;
          const mediaStore = tx.objectStore('mediaBlobs');
          if (!mediaStore.indexNames.contains('priority')) {
            mediaStore.createIndex('priority', 'priority');
          }
          if (!mediaStore.indexNames.contains('lastPlayed')) {
            mediaStore.createIndex('lastPlayed', 'lastPlayed');
          }
          if (!mediaStore.indexNames.contains('etag')) {
            mediaStore.createIndex('etag', 'etag');
          }
        }

        // Store for stream queue state
        if (!db.objectStoreNames.contains('streamQueue')) {
          const queueStore = db.createObjectStore('streamQueue', { keyPath: 'screenId' });
          queueStore.createIndex('timestamp', 'timestamp');
        }

        console.log(`${this.logPrefix} Database schema created/updated (v${this.dbVersion})`);
      };
    });

    return this.initPromise;
  }

  /**
   * Ensure database is initialized before operations
   */
  async ensureInitialized() {
    if (!this.isInitialized) {
      await this.init();
      // Rebuild in-memory VAST blob map from IndexedDB so synchronous lookups work immediately
      try {
        await this._rebuildVastBlobMap();
      } catch (e) {
        console.warn(this.logPrefix + ' Failed to rebuild VAST blob map:', e.message);
      }
    }
  }

  // ============================================================
  // API Response Caching
  // ============================================================

  /**
   * Cache an API response
   */
  async cacheAPIResponse(endpoint, data, screenId = null) {
    await this.ensureInitialized();

    try {
      const tx = this.db.transaction('apiResponses', 'readwrite');
      const store = tx.objectStore('apiResponses');

      await this._promisifyRequest(store.put({
        endpoint,
        data,
        screenId,
        timestamp: Date.now()
      }));

      console.log(`${this.logPrefix} Cached API response:`, endpoint);
      return true;
    } catch (error) {
      console.error(`${this.logPrefix} Failed to cache API response:`, error);
      return false;
    }
  }

  /**
   * Get cached API response
   */
  async getCachedResponse(endpoint, maxAge = null) {
    await this.ensureInitialized();
    const ttl = maxAge || this.defaultTTL.apiResponse;

    try {
      const tx = this.db.transaction('apiResponses', 'readonly');
      const store = tx.objectStore('apiResponses');
      const result = await this._promisifyRequest(store.get(endpoint));

      if (result && (Date.now() - result.timestamp) < ttl) {
        console.log(`${this.logPrefix} Cache hit for:`, endpoint);
        return result.data;
      }

      console.log(`${this.logPrefix} Cache miss or expired for:`, endpoint);
      return null;
    } catch (error) {
      console.error(`${this.logPrefix} Failed to get cached response:`, error);
      return null;
    }
  }

  // ============================================================
  // Media Blob Caching (Enhanced with priority and eviction)
  // ============================================================

  /**
   * Get storage quota info for adaptive caching
   */
  async getStorageQuota() {
    try {
      if ('storage' in navigator && navigator.storage.estimate) {
        const estimate = await navigator.storage.estimate();
        return {
          quota: estimate.quota || 0,
          usage: estimate.usage || 0,
          available: (estimate.quota || 0) - (estimate.usage || 0)
        };
      }
    } catch (e) {
      console.warn(`${this.logPrefix} Failed to get storage quota:`, e);
    }
    return { quota: 0, usage: 0, available: 0 };
  }

  /**
   * Get max file size based on device storage capacity
   */
  async getMaxFileSize() {
    const { quota } = await this.getStorageQuota();
    const quotaMB = quota / (1024 * 1024);

    if (quotaMB >= 512) {
      return this.storageThresholds.maxFileSize.high; // 100MB
    } else if (quotaMB >= 256) {
      return this.storageThresholds.maxFileSize.medium; // 30MB
    } else {
      return this.storageThresholds.maxFileSize.low; // 5MB
    }
  }

  /**
   * Check if a URL should be cached based on content type and size
   */
  async shouldCacheMedia(url, size = null, contentType = null) {
    // Don't cache external streams
    const skipPatterns = ['youtube.com', 'youtu.be', 'twitch.tv', 'vimeo.com'];
    if (skipPatterns.some(p => url.includes(p))) {
      return false;
    }

    // Check file size if provided
    if (size) {
      const maxSize = await this.getMaxFileSize();
      if (size > maxSize) {
        console.log(`${this.logPrefix} Skipping cache for large file: ${this._formatBytes(size)} > ${this._formatBytes(maxSize)}`);
        return false;
      }
    }

    return true;
  }

  getMediaTTL(priority = 3) {
    return this.mediaTTLByPriority[priority] || this.defaultTTL.mediaBlob;
  }

  /**
   * Cache a media blob with priority and ETag support
   * @param {string} url - Media URL
   * @param {Object} options - Caching options
   * @param {number} options.priority - 1=paid ads, 2=self-promo, 3=default streams
   * @param {string} options.etag - ETag from server for freshness validation
   * @param {string} options.lastModified - Last-Modified header value
   * @param {number} options.expectedSize - Expected file size (for pre-validation)
   */
  async cacheMediaBlob(url, options = {}) {
    await this.ensureInitialized();
    const { priority = 3, etag = null, lastModified = null, expectedSize = null } = options;

    try {
      // Check if we should cache this URL
      if (!await this.shouldCacheMedia(url, expectedSize)) {
        this.metrics.misses++;
        return url; // Return original URL
      }

      // Check if already cached with valid TTL
      const existingRecord = await this._getMediaRecord(url);
      if (existingRecord) {
        const ttl = this.getMediaTTL(existingRecord.priority);
        if ((Date.now() - existingRecord.timestamp) < ttl) {
          // Update lastPlayed timestamp
          await this._updateMediaMetadata(url, { lastPlayed: Date.now() });
          this.metrics.hits++;
          this.metrics.bytesServedFromCache += existingRecord.size || 0;
          return this.createTrackedBlobUrl(existingRecord.blob, url, existingRecord.size);
        }
      }

      // Check storage quota before fetching
      await this.evictIfNeeded();

      // Fetch the media (rewrite CDN URLs to same-origin /media/ path)
      const fetchUrl = this._toSameOriginUrl(url);
      const response = await this._fetchWithRetry(fetchUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      const blob = await response.blob();
      const type = blob.type || this._getMediaType(url);

      // Check size against limits
      const maxSize = await this.getMaxFileSize();
      if (blob.size > maxSize) {
        console.log(`${this.logPrefix} File too large after download, not caching: ${this._formatBytes(blob.size)}`);
        this.metrics.bytesFetchedFromNetwork += blob.size;
        return url;
      }

      // Get ETag and Last-Modified from response if not provided
      const responseEtag = etag || response.headers.get('etag');
      const responseLastModified = lastModified || response.headers.get('last-modified');

      const tx = this.db.transaction('mediaBlobs', 'readwrite');
      const store = tx.objectStore('mediaBlobs');

      const now = Date.now();
      await this._promisifyRequest(store.put({
        url,
        blob,
        type,
        size: blob.size,
        priority,
        etag: responseEtag,
        lastModified: responseLastModified,
        timestamp: now,
        lastPlayed: now,
        lastValidated: now
      }));

      this.metrics.bytesFetchedFromNetwork += blob.size;
      console.log(`${this.logPrefix} Cached media blob:`, url, `(${this._formatBytes(blob.size)}, priority=${priority})`);
      return this.createTrackedBlobUrl(blob, url, blob.size);
    } catch (error) {
      this.metrics.errors++;
      console.warn(`${this.logPrefix} Failed to cache media:`, url, error);
      return url; // Fallback to original URL
    }
  }

  /**
   * Get cached media blob URL with metrics tracking
   */
  async getCachedMediaUrl(url) {
    await this.ensureInitialized();

    try {
      const record = await this._getMediaRecord(url);

      if (record) {
        const ttl = this.getMediaTTL(record.priority);
        if ((Date.now() - record.timestamp) < ttl) {
          // Validate blob integrity
          if (!record.blob || record.blob.size === 0) {
            console.warn(`${this.logPrefix} Corrupted cache entry detected:`, url);
            await this.removeMediaFromCache(url);
            this.metrics.misses++;
            return null;
          }

          // Update lastPlayed timestamp
          await this._updateMediaMetadata(url, { lastPlayed: Date.now() });
          this.metrics.hits++;
          this.metrics.bytesServedFromCache += record.size || 0;
          return this.createTrackedBlobUrl(record.blob, url, record.size);
        }
      }

      this.metrics.misses++;
      return null;
    } catch (error) {
      this.metrics.errors++;
      console.error(`${this.logPrefix} Failed to get cached media:`, error);
      return null;
    }
  }

  async getCachedMediaRecord(url) {
    await this.ensureInitialized();

    try {
      const record = await this._getMediaRecord(url);
      if (!record) return null;

      const ttl = this.getMediaTTL(record.priority);
      if ((Date.now() - record.timestamp) >= ttl) {
        return null;
      }

      if (!record.blob || record.blob.size === 0) {
        return null;
      }

      return record;
    } catch (_) {
      return null;
    }
  }

  /**
   * Get the raw media record (for ETag validation)
   */
  async _getMediaRecord(url) {
    try {
      const tx = this.db.transaction('mediaBlobs', 'readonly');
      const store = tx.objectStore('mediaBlobs');
      return await this._promisifyRequest(store.get(url));
    } catch (error) {
      return null;
    }
  }

  /**
   * Update metadata for a cached media item
   */
  async _updateMediaMetadata(url, updates) {
    try {
      const tx = this.db.transaction('mediaBlobs', 'readwrite');
      const store = tx.objectStore('mediaBlobs');
      const record = await this._promisifyRequest(store.get(url));

      if (record) {
        Object.assign(record, updates);
        await this._promisifyRequest(store.put(record));
      }
    } catch (error) {
      // Non-critical - silently fail
    }
  }

  /**
   * Remove a specific media item from cache
   */
  async removeMediaFromCache(url) {
    try {
      const tx = this.db.transaction('mediaBlobs', 'readwrite');
      const store = tx.objectStore('mediaBlobs');
      await this._promisifyRequest(store.delete(url));
      console.log(`${this.logPrefix} Removed from cache:`, url);
    } catch (error) {
      console.warn(`${this.logPrefix} Failed to remove from cache:`, url, error);
    }
  }

  /**
   * Validate cached content freshness using HEAD request with ETag.
   * Throttled: skips validation if last validated within _validationThrottleMs.
   */
  async validateCachedMedia(url) {
    try {
      const record = await this._getMediaRecord(url);
      if (!record) return { valid: false, reason: 'not_cached' };

      // Throttle: skip HEAD request if validated recently
      if (record.lastValidated && (Date.now() - record.lastValidated) < this._validationThrottleMs) {
        return { valid: true, reason: 'throttled' };
      }

      const fetchUrl = this._toSameOriginUrl(url);
      if (this._shouldSkipHeadValidation(fetchUrl)) {
        await this._updateMediaMetadata(url, { lastValidated: Date.now() });
        return { valid: true, reason: 'cross_origin_validation_skipped' };
      }

      const headers = {};
      if (record.etag) headers['If-None-Match'] = record.etag;
      if (record.lastModified) headers['If-Modified-Since'] = record.lastModified;

      const response = await this._fetchWithRetry(fetchUrl, { method: 'HEAD', headers }, 1);

      if (response.status === 304) {
        // Content unchanged - update validation timestamp
        await this._updateMediaMetadata(url, { lastValidated: Date.now() });
        return { valid: true, unchanged: true };
      } else if (response.status === 200) {
        // Content changed - mark for re-download
        return { valid: false, reason: 'content_changed', newEtag: response.headers.get('etag') };
      }

      return { valid: true }; // Assume valid on other responses
    } catch (error) {
      // Validation failed - assume cached version is still okay
      console.warn(`${this.logPrefix} Background validation failed:`, url);
      return { valid: true, reason: 'validation_failed' };
    }
  }

  // ============================================================
  // Eviction Logic (Priority-Weighted LRU)
  // ============================================================

  /**
   * Evict cached media if storage is running low
   */
  async evictIfNeeded(targetFreeBytes = null) {
    const { quota, usage, available } = await this.getStorageQuota();
    if (!quota) return; // Can't determine quota

    const usageRatio = usage / quota;
    if (usageRatio < this.storageThresholds.evictionTrigger) {
      return; // Still have space
    }

    const targetFree = targetFreeBytes || quota * this.storageThresholds.targetFree;
    console.log(`${this.logPrefix} Starting eviction: ${Math.round(usageRatio * 100)}% used, target ${this._formatBytes(targetFree)} free`);

    await this._runEviction(targetFree - available);
  }

  /**
   * Run the eviction process
   */
  async _runEviction(bytesToFree) {
    if (bytesToFree <= 0) return;

    await this.ensureInitialized();

    try {
      // Get all cached items
      const items = await this._getAllMediaRecords();

      // Calculate eviction score for each item
      // Higher score = more likely to evict
      const now = Date.now();
      const scored = items.map(item => {
        const lastPlayed = item.lastPlayed || item.timestamp;
        const age = now - lastPlayed;
        const weight = this.priorityWeights[item.priority] || 1;

        // Score = age / weight (older items with lower priority have higher scores)
        return {
          ...item,
          evictionScore: age / weight
        };
      });

      // Sort by eviction score (highest first = evict first)
      scored.sort((a, b) => b.evictionScore - a.evictionScore);

      let freedBytes = 0;
      for (const item of scored) {
        if (freedBytes >= bytesToFree) break;

        await this.removeMediaFromCache(item.url);
        freedBytes += item.size || 0;
        this.metrics.evictions++;
        console.log(`${this.logPrefix} Evicted:`, item.url, `(${this._formatBytes(item.size)}, score=${Math.round(item.evictionScore / 1000)})`);
      }

      console.log(`${this.logPrefix} Eviction complete: freed ${this._formatBytes(freedBytes)}`);
    } catch (error) {
      console.error(`${this.logPrefix} Eviction failed:`, error);
    }
  }

  /**
   * Get all media records for eviction analysis
   */
  async _getAllMediaRecords() {
    const items = [];
    try {
      const tx = this.db.transaction('mediaBlobs', 'readonly');
      const store = tx.objectStore('mediaBlobs');
      const cursor = store.openCursor();

      await this._processCursor(cursor, (c) => {
        items.push({
          url: c.value.url,
          size: c.value.size || 0,
          priority: c.value.priority || 3,
          timestamp: c.value.timestamp,
          lastPlayed: c.value.lastPlayed
        });
      });
    } catch (error) {
      console.error(`${this.logPrefix} Failed to get media records:`, error);
    }
    return items;
  }

  // ============================================================
  // Stream Queue State
  // ============================================================

  /**
   * Save stream queue state for a screen
   */
  async saveStreamQueue(screenId, queueData) {
    await this.ensureInitialized();

    try {
      const tx = this.db.transaction('streamQueue', 'readwrite');
      const store = tx.objectStore('streamQueue');

      await this._promisifyRequest(store.put({
        screenId,
        queue: queueData.queue || [],
        currentIndex: queueData.currentIndex || 0,
        currentPosition: queueData.currentPosition || 0,
        timestamp: Date.now()
      }));

      console.log(`${this.logPrefix} Saved stream queue for screen:`, screenId);
      return true;
    } catch (error) {
      console.error(`${this.logPrefix} Failed to save stream queue:`, error);
      return false;
    }
  }

  /**
   * Get stream queue state for a screen
   */
  async getStreamQueue(screenId) {
    await this.ensureInitialized();

    try {
      const tx = this.db.transaction('streamQueue', 'readonly');
      const store = tx.objectStore('streamQueue');
      const result = await this._promisifyRequest(store.get(screenId));

      if (result && (Date.now() - result.timestamp) < this.defaultTTL.streamQueue) {
        return result;
      }

      return null;
    } catch (error) {
      console.error(`${this.logPrefix} Failed to get stream queue:`, error);
      return null;
    }
  }

  // ============================================================
  // Maintenance Operations
  // ============================================================

  /**
   * Clean up expired entries
   */
  async cleanup() {
    await this.ensureInitialized();

    const now = Date.now();
    let cleanedCount = 0;

    try {
      // Clean API responses
      const apiTx = this.db.transaction('apiResponses', 'readwrite');
      const apiStore = apiTx.objectStore('apiResponses');
      const apiCursor = apiStore.openCursor();

      await this._processCursor(apiCursor, async (cursor) => {
        if (now - cursor.value.timestamp > this.defaultTTL.apiResponse) {
          await this._promisifyRequest(cursor.delete());
          cleanedCount++;
        }
      });

      // Clean media blobs
      const mediaTx = this.db.transaction('mediaBlobs', 'readwrite');
      const mediaStore = mediaTx.objectStore('mediaBlobs');
      const mediaCursor = mediaStore.openCursor();

      await this._processCursor(mediaCursor, async (cursor) => {
        const ttl = this.getMediaTTL(cursor.value.priority);
        if (now - cursor.value.timestamp > ttl) {
          await this._promisifyRequest(cursor.delete());
          cleanedCount++;
        }
      });

      console.log(`${this.logPrefix} Cleanup complete, removed ${cleanedCount} entries`);
    } catch (error) {
      console.error(`${this.logPrefix} Cleanup failed:`, error);
    }
  }

  /**
   * Get cache statistics including metrics
   */
  async getStats() {
    await this.ensureInitialized();

    const stats = {
      apiResponses: { count: 0, size: 0 },
      mediaBlobs: { count: 0, size: 0, byPriority: { 1: 0, 2: 0, 3: 0 } },
      streamQueue: { count: 0 },
      metrics: { ...this.metrics },
      storage: await this.getStorageQuota()
    };

    try {
      // Count API responses
      const apiTx = this.db.transaction('apiResponses', 'readonly');
      stats.apiResponses.count = await this._promisifyRequest(
        apiTx.objectStore('apiResponses').count()
      );

      // Count and size media blobs by priority
      const mediaTx = this.db.transaction('mediaBlobs', 'readonly');
      const mediaStore = mediaTx.objectStore('mediaBlobs');
      const mediaCursor = mediaStore.openCursor();

      await this._processCursor(mediaCursor, (cursor) => {
        stats.mediaBlobs.count++;
        stats.mediaBlobs.size += cursor.value.size || 0;
        const priority = cursor.value.priority || 3;
        stats.mediaBlobs.byPriority[priority] = (stats.mediaBlobs.byPriority[priority] || 0) + 1;
      });

      // Count stream queues
      const queueTx = this.db.transaction('streamQueue', 'readonly');
      stats.streamQueue.count = await this._promisifyRequest(
        queueTx.objectStore('streamQueue').count()
      );

      stats.mediaBlobs.sizeFormatted = this._formatBytes(stats.mediaBlobs.size);

      // Calculate hit rate
      const totalRequests = stats.metrics.hits + stats.metrics.misses;
      stats.metrics.hitRate = totalRequests > 0 ? (stats.metrics.hits / totalRequests) : 0;
      stats.metrics.hitRateFormatted = `${Math.round(stats.metrics.hitRate * 100)}%`;
      stats.metrics.bandwidthSavedFormatted = this._formatBytes(stats.metrics.bytesServedFromCache);

    } catch (error) {
      console.error(`${this.logPrefix} Failed to get stats:`, error);
    }

    return stats;
  }

  /**
   * Get metrics for API reporting
   */
  getMetrics() {
    const totalRequests = this.metrics.hits + this.metrics.misses;
    return {
      hits: this.metrics.hits,
      misses: this.metrics.misses,
      hitRate: totalRequests > 0 ? (this.metrics.hits / totalRequests) : 0,
      bytesServedFromCache: this.metrics.bytesServedFromCache,
      bytesFetchedFromNetwork: this.metrics.bytesFetchedFromNetwork,
      evictions: this.metrics.evictions,
      errors: this.metrics.errors,
      bandwidthSaved: this.metrics.bytesServedFromCache,
      bandwidthSavedFormatted: this._formatBytes(this.metrics.bytesServedFromCache)
    };
  }

  /**
   * Reset metrics (typically after reporting to server)
   */
  resetMetrics() {
    this.metrics = {
      hits: 0,
      misses: 0,
      bytesServedFromCache: 0,
      bytesFetchedFromNetwork: 0,
      evictions: 0,
      errors: 0
    };
  }

  /**
   * Report metrics to API endpoint
   */
  async reportMetrics(screenId) {
    if (!screenId) return;

    const metrics = this.getMetrics();
    const connection = typeof navigator !== 'undefined'
      ? (navigator.connection || navigator.mozConnection || navigator.webkitConnection || null)
      : null;
    const reportingContext = {
      deviceClass: this.deviceClass || 'unknown',
      connectionType: (typeof window !== 'undefined' && window.__TRILL_CONNECTION_TYPE__) || connection?.type || 'unknown',
      effectiveType: connection?.effectiveType || null,
      bandwidthMbps: typeof connection?.downlink === 'number' ? connection.downlink : null,
      latencyRtt: typeof connection?.rtt === 'number' ? connection.rtt : null,
      saveData: Boolean(connection?.saveData),
    };

    // Only report if we have meaningful data
    if (metrics.hits === 0 && metrics.misses === 0) return;

    try {
      const apiBase = (typeof window !== 'undefined' && window.__TRILLBOARDS_API_BASE) || 'https://api.trillboards.com';
      await fetch(`${apiBase}/v2/earner/screen-metrics/${screenId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'media_cache',
          data: {
            ...metrics,
            ...reportingContext,
          },
          timestamp: new Date().toISOString()
        })
      });

      console.log(`${this.logPrefix} Reported metrics:`, metrics.hitRateFormatted, 'hit rate,', metrics.bandwidthSavedFormatted, 'saved');
      if (!(typeof window !== 'undefined' && window.TrillboardsBandwidthTracker)) {
        this.resetMetrics();
      }
    } catch (error) {
      // Non-critical - silently fail
      console.warn(`${this.logPrefix} Failed to report metrics:`, error);
    }
  }

  /**
   * Clear all cached data
   */
  async clearAll() {
    await this.ensureInitialized();

    try {
      const tx = this.db.transaction(
        ['apiResponses', 'mediaBlobs', 'streamQueue'],
        'readwrite'
      );

      await Promise.all([
        this._promisifyRequest(tx.objectStore('apiResponses').clear()),
        this._promisifyRequest(tx.objectStore('mediaBlobs').clear()),
        this._promisifyRequest(tx.objectStore('streamQueue').clear())
      ]);

      console.log(`${this.logPrefix} All caches cleared`);
      return true;
    } catch (error) {
      console.error(`${this.logPrefix} Failed to clear caches:`, error);
      return false;
    }
  }

  // ============================================================
  // Helper Methods
  // ============================================================

  _promisifyRequest(request) {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async _processCursor(cursorRequest, callback) {
    return new Promise((resolve, reject) => {
      cursorRequest.onsuccess = async (event) => {
        const cursor = event.target.result;
        if (cursor) {
          await callback(cursor);
          cursor.continue();
        } else {
          resolve();
        }
      };
      cursorRequest.onerror = () => reject(cursorRequest.error);
    });
  }

  _getMediaType(url) {
    let pathname = typeof url === 'string' ? url : '';
    try {
      pathname = new URL(pathname, window.location.href).pathname;
    } catch (_) {
      pathname = pathname.split(/[?#]/)[0];
    }
    const ext = pathname.split('.').pop()?.toLowerCase();
    const types = {
      mp4: 'video/mp4',
      webm: 'video/webm',
      wmv: 'video/x-ms-wmv',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      svg: 'image/svg+xml'
    };
    return types[ext] || 'application/octet-stream';
  }

  _supportsSameOriginMediaProxy() {
    if (typeof window === 'undefined') return false;
    if (window.__TRILLBOARDS_MEDIA_PROXY_ENABLED__ === true) return true;
    return window.location.hostname === 'screen.trillboards.com';
  }

  /**
   * Convert CDN URLs to same-origin /media/ paths.
   * Only production screen.trillboards.com currently serves that proxy path.
   * QA screens must keep the original CDN URL or uploaded media will 404.
   *
   * @param {string} url - Original URL (may be cdn.trillboards.com)
   * @returns {string} Same-origin path if applicable, otherwise original URL
   */
  _toSameOriginUrl(url) {
    if (!url || typeof url !== 'string') return url;
    try {
      const parsed = new URL(url, window.location.origin);
      if (
        this._supportsSameOriginMediaProxy() &&
        parsed.hostname === 'cdn.trillboards.com' &&
        !parsed.pathname.startsWith('/transform')
      ) {
        return '/media' + parsed.pathname + parsed.search;
      }
    } catch (e) {
      // Invalid URL — return as-is
    }
    return url;
  }

  _shouldSkipHeadValidation(url) {
    if (!url || typeof url !== 'string' || typeof window === 'undefined') return false;
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin !== window.location.origin;
    } catch (e) {
      return false;
    }
  }

  /**
   * Fetch with retry, request deduplication, and 429 backoff.
   * Prevents concurrent fetches for the same URL and respects Retry-After headers.
   *
   * @param {string} url - URL to fetch (already rewritten via _toSameOriginUrl)
   * @param {Object} [opts] - fetch options
   * @param {number} [maxRetries=2] - max retry attempts
   * @returns {Promise<Response>}
   */
  async _fetchWithRetry(url, opts, maxRetries) {
    if (maxRetries === undefined) maxRetries = 2;

    // Request deduplication — if an identical fetch is already in-flight, share its promise
    var existing = this._inflight.get(url);
    if (existing) {
      return existing.then(function(r) { return r.clone(); });
    }

    var self = this;
    var attempt = function(retryCount) {
      return fetch(url, opts).then(function(response) {
        self._inflight.delete(url);

        if (response.status === 429 && retryCount < maxRetries) {
          var retryAfter = parseInt(response.headers.get('Retry-After') || '0', 10);
          var delay = retryAfter > 0 ? retryAfter * 1000 : Math.pow(2, retryCount) * 1000;
          console.log(self.logPrefix + ' 429 rate-limited, retrying in ' + delay + 'ms: ' + url.substring(0, 60));
          return new Promise(function(resolve) {
            setTimeout(function() { resolve(attempt(retryCount + 1)); }, delay);
          });
        }

        return response;
      }).catch(function(error) {
        self._inflight.delete(url);
        if (retryCount < maxRetries) {
          var delay = Math.pow(2, retryCount) * 1000;
          console.log(self.logPrefix + ' Fetch failed, retrying in ' + delay + 'ms: ' + url.substring(0, 60));
          return new Promise(function(resolve) {
            setTimeout(function() { resolve(attempt(retryCount + 1)); }, delay);
          });
        }
        throw error;
      });
    };

    var promise = attempt(0);
    this._inflight.set(url, promise);
    return promise;
  }

  _formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // ============================================================
  // Device Class Detection & Adaptive Storage Limits
  // ============================================================

  /**
   * Detect device class from user agent for adaptive storage limits
   * @returns {string} Device class identifier
   */
  _detectDeviceClass() {
    const ua = navigator.userAgent || '';

    // Fire TV devices
    if (/AFT[A-Z]|Fire TV/i.test(ua)) {
      // Fire TV Cube and higher-end models (AFTN, AFTKA)
      if (/AFTN|AFTKA|AFTT/i.test(ua)) {
        return 'fire_tv_high';
      }
      return 'fire_tv_low';
    }

    // Samsung Tizen TVs
    if (/Tizen/i.test(ua)) {
      return 'samsung_tv';
    }

    // LG webOS TVs
    if (/webOS|NetCast/i.test(ua)) {
      return 'lg_tv';
    }

    // Android WebView (partner shells like com.juuce.adshell)
    // Standard Android WebView UA contains '; wv)' after the Android version
    if (/; wv\)/.test(ua)) {
      return 'android_webview';
    }

    // Android tablets (touch-enabled Android devices)
    if (/Android/i.test(ua) && 'ontouchstart' in window) {
      return 'android_tablet';
    }

    // Chromecast/Google TV
    if (/CrKey|Chromecast|GoogleTV/i.test(ua)) {
      return 'chromecast';
    }

    // Default to Chrome/desktop (most signage runs on desktop browsers)
    return 'default';
  }

  /**
   * Get device class (cached from construction)
   * @returns {string} Device class identifier
   */
  getDeviceClass() {
    return this.deviceClass;
  }

  /**
   * Get storage limits appropriate for the detected device class
   * These limits are tuned based on typical storage availability
   * and memory constraints of each device type.
   * @returns {{ totalCache: number, maxFile: number, vastMaxFile: number }}
   */
  getStorageLimitsForDevice() {
    const limits = {
      // Fire TV Stick (basic) - limited storage
      fire_tv_low: {
        totalCache: 200 * 1024 * 1024,    // 200MB total
        maxFile: 30 * 1024 * 1024,        // 30MB max per file
        vastMaxFile: 30 * 1024 * 1024     // 30MB max for VAST videos
      },
      // Fire TV Cube/4K - more storage available
      fire_tv_high: {
        totalCache: 400 * 1024 * 1024,    // 400MB total
        maxFile: 50 * 1024 * 1024,        // 50MB max per file
        vastMaxFile: 50 * 1024 * 1024     // 50MB max for VAST videos
      },
      // Samsung Tizen TVs - moderate storage
      samsung_tv: {
        totalCache: 300 * 1024 * 1024,    // 300MB total
        maxFile: 50 * 1024 * 1024,        // 50MB max per file
        vastMaxFile: 50 * 1024 * 1024     // 50MB max for VAST videos
      },
      // LG webOS TVs - limited storage
      lg_tv: {
        totalCache: 200 * 1024 * 1024,    // 200MB total
        maxFile: 30 * 1024 * 1024,        // 30MB max per file
        vastMaxFile: 30 * 1024 * 1024     // 30MB max for VAST videos
      },
      // Android WebView (partner WebView shells) — bandwidth-constrained but IndexedDB quota is 100MB+
      android_webview: {
        totalCache: 200 * 1024 * 1024,    // 200MB total (matches fire_tv_low)
        maxFile: 30 * 1024 * 1024,        // 30MB max per file (was 10MB — rejected 10% of ads)
        vastMaxFile: 30 * 1024 * 1024     // 30MB max for VAST videos (matches fire_tv_low)
      },
      // Android tablets - varies but usually limited
      android_tablet: {
        totalCache: 150 * 1024 * 1024,    // 150MB total
        maxFile: 20 * 1024 * 1024,        // 20MB max per file
        vastMaxFile: 20 * 1024 * 1024     // 20MB max for VAST videos
      },
      // Chromecast/Google TV
      chromecast: {
        totalCache: 200 * 1024 * 1024,    // 200MB total
        maxFile: 30 * 1024 * 1024,        // 30MB max per file
        vastMaxFile: 30 * 1024 * 1024     // 30MB max for VAST videos
      },
      // Chrome/Desktop - generous storage
      default: {
        totalCache: 500 * 1024 * 1024,    // 500MB total
        maxFile: 100 * 1024 * 1024,       // 100MB max per file
        vastMaxFile: 100 * 1024 * 1024    // 100MB max for VAST videos
      }
    };

    return limits[this.deviceClass] || limits.default;
  }

  // ============================================================
  // VAST/IMA Ad Video Caching
  // ============================================================

  /**
   * Cache a VAST video with priority 1.5 (between paid ads and self-promo)
   * Designed for IMA SDK video ads to reduce bandwidth.
   *
   * @param {string} url - VAST media URL to cache
   * @param {Object} options - Optional settings
   * @param {number} options.expectedSize - Expected file size in bytes (for pre-validation)
   * @returns {Promise<{ blobUrl: string|null, fromCache: boolean, size: number }>}
   */
  async cacheVastMedia(url, options = {}) {
    const { expectedSize = null, creativeId = null } = options;

    // Use creativeId as cache key when available (stable across URL changes).
    // googlevideo.com generates unique URLs per request (different expire, sig, cpn tokens),
    // so the full URL never matches on replay. creativeId from IMA's ad.getAdId() is stable.
    const cacheKey = creativeId ? ('vast:' + creativeId) : url;

    // Skip if URL is not valid or is a tracking pixel
    if (!url || typeof url !== 'string' || url.length < 10) {
      return { blobUrl: null, fromCache: false, size: 0, error: 'invalid_url' };
    }

    // Skip tracking/beacon URLs (they're small and shouldn't be cached)
    if (/beacon|impression|track|pixel|log\./i.test(url)) {
      return { blobUrl: null, fromCache: false, size: 0, error: 'tracking_url' };
    }

    // Inflight dedup by cacheKey — if already fetching this creative, return the in-flight promise.
    // Without this, LOADED and STARTED both call cacheVastMedia() for the same creative,
    // causing two network fetches since the URL-based _inflight dedup doesn't help
    // (googlevideo generates unique URLs per request).
    if (this._vastInflight.has(cacheKey)) {
      return this._vastInflight.get(cacheKey);
    }

    const startTime = Date.now();
    const deviceLimits = this.getStorageLimitsForDevice();

    const promise = (async () => {
      try {
        await this.ensureInitialized();

        // Check if already cached with valid TTL (by creativeId or URL)
        const existingRecord = await this._getMediaRecord(cacheKey);
        if (existingRecord) {
          const age = Date.now() - existingRecord.timestamp;
          if (age < this.vastTTL) {
            // Validate blob integrity
            if (existingRecord.blob && existingRecord.blob.size > 0) {
              // Update lastPlayed timestamp
              await this._updateMediaMetadata(cacheKey, { lastPlayed: Date.now() });
              this.metrics.hits++;
              this.metrics.bytesServedFromCache += existingRecord.size || 0;

              const blobUrl = this.createTrackedBlobUrl(existingRecord.blob, cacheKey, existingRecord.size);
              // Update in-memory map for synchronous lookups
              this._vastBlobMap.set(cacheKey, blobUrl);

              console.log(`${this.logPrefix} VAST cache hit: ${cacheKey} (${this._formatBytes(existingRecord.size)})`);

              return {
                blobUrl,
                fromCache: true,
                size: existingRecord.size || 0
              };
            } else {
              // Corrupted entry - remove it
              await this.removeMediaFromCache(cacheKey);
              this._vastBlobMap.delete(cacheKey);
            }
          }
        }

        // Pre-check size if provided
        if (expectedSize && expectedSize > deviceLimits.vastMaxFile) {
          console.log(`${this.logPrefix} VAST video too large to cache (pre-check):`, this._formatBytes(expectedSize));
          this.metrics.misses++;
          return { blobUrl: null, fromCache: false, size: 0, error: 'too_large' };
        }

        // Rewrite Trillboards CDN URLs to same-origin (avoids CORS overhead for our own content).
        // All other URLs (googlevideo.com, partner domains, etc.) are fetched as-is.
        // If a URL lacks CORS headers, fetch() fails and the catch block handles it gracefully.
        let fetchUrl;
        try {
          fetchUrl = this._toSameOriginUrl(url);
        } catch (urlErr) {
          return { blobUrl: null, fromCache: false, size: 0, error: 'invalid_url_parse' };
        }

        // Check storage quota before fetching
        await this.evictIfNeeded();

        // Cross-origin ad videos (Google CDN, partner CDNs) can't be fetched
        // directly via CORS. Instead, ask the API for a permanent CDN URL:
        //   cdn.trillboards.com/creative-media/{creativeId}.mp4
        // The API caches creatives to S3 on every VAST completion (fire-and-forget).
        // _toSameOriginUrl() rewrites CDN URLs to /media/* (CloudFront multi-origin),
        // making the fetch same-origin with full blob access for IndexedDB storage.
        const isCrossOrigin = fetchUrl.startsWith('http') && !fetchUrl.startsWith(window.location.origin);
        if (isCrossOrigin && creativeId) {
          try {
            const apiBase = (typeof window !== 'undefined' && window.__TRILLBOARDS_API_BASE) || 'https://api.trillboards.com';
            const resolveResp = await fetch(`${apiBase}/openrtb/v1/vast-cache/resolve/${encodeURIComponent(creativeId)}`);
            if (resolveResp.ok) {
              const resolveData = await resolveResp.json();
              if (resolveData.cdnUrl) {
                // Rewrite CDN URL to same-origin /media/ path
                fetchUrl = this._toSameOriginUrl(resolveData.cdnUrl);
                console.log(`${this.logPrefix} Resolved creative ${creativeId} → ${fetchUrl}`);
                // Fall through to the normal fetch + IndexedDB store below
              } else {
                // Creative not yet cached on S3 (first play, race condition).
                // Will be available on the next play after the VAST completion handler
                // fires cacheCreativeMedia() on the API side.
                return { blobUrl: null, fromCache: false, size: 0, error: 'not_yet_cached' };
              }
            } else {
              return { blobUrl: null, fromCache: false, size: 0, error: 'resolve_http_' + resolveResp.status };
            }
          } catch (resolveErr) {
            // API unreachable — degrade gracefully, no caching this time
            return { blobUrl: null, fromCache: false, size: 0, error: 'resolve_failed' };
          }
        } else if (isCrossOrigin) {
          // Cross-origin without a creativeId — can't resolve, skip
          return { blobUrl: null, fromCache: false, size: 0, error: 'cross_origin_no_id' };
        }
        const response = await this._fetchWithRetry(fetchUrl, {});
        if (!response.ok) {
          throw new Error(`Failed to fetch VAST video: ${response.status}`);
        }

        const blob = await response.blob();
        const type = blob.type || 'video/mp4';

        // Validate size against device limits
        if (blob.size > deviceLimits.vastMaxFile) {
          console.log(`${this.logPrefix} VAST video too large after fetch, not caching:`, this._formatBytes(blob.size));
          this.metrics.bytesFetchedFromNetwork += blob.size;
          this.metrics.misses++;
          // Return blob URL anyway so video can still play
          return {
            blobUrl: this.createTrackedBlobUrl(blob, cacheKey, blob.size),
            fromCache: false,
            size: blob.size,
            error: 'too_large_after_fetch'
          };
        }

        // Store in IndexedDB with creativeId-based key and VAST priority (1.5)
        const tx = this.db.transaction('mediaBlobs', 'readwrite');
        const store = tx.objectStore('mediaBlobs');
        const now = Date.now();

        await this._promisifyRequest(store.put({
          url: cacheKey,        // keyPath uses creativeId-based key (e.g. 'vast:709996466052')
          originalUrl: url,     // Keep original URL for debugging/telemetry
          creativeId: creativeId,
          blob,
          type,
          size: blob.size,
          priority: 1.5,  // VAST priority (between paid ads and self-promo)
          etag: response.headers.get('etag'),
          lastModified: response.headers.get('last-modified'),
          timestamp: now,
          lastPlayed: now,
          lastValidated: now,
          isVast: true  // Tag as VAST for analytics
        }));

        this.metrics.bytesFetchedFromNetwork += blob.size;

        const blobUrl = this.createTrackedBlobUrl(blob, cacheKey, blob.size);
        // Update in-memory map for synchronous lookups
        this._vastBlobMap.set(cacheKey, blobUrl);

        console.log(`${this.logPrefix} Cached VAST video: ${cacheKey} (${this._formatBytes(blob.size)}, ${Date.now() - startTime}ms)`);

        return {
          blobUrl,
          fromCache: false,
          size: blob.size
        };
      } catch (error) {
        this.metrics.errors++;
        console.warn(`${this.logPrefix} Failed to cache VAST video: ${cacheKey}`, error.message);
        return { blobUrl: null, fromCache: false, size: 0, error: error.message };
      }
    })();

    this._vastInflight.set(cacheKey, promise);
    promise.finally(() => { this._vastInflight.delete(cacheKey); });
    return promise;
  }

  /**
   * Rebuild the in-memory VAST blob map from IndexedDB.
   * Called once during ensureInitialized() so that synchronous lookups
   * via getVastBlobUrl() work immediately after page load.
   */
  async _rebuildVastBlobMap() {
    if (!this.db) return;
    this._vastBlobMap.clear();
    const tx = this.db.transaction('mediaBlobs', 'readonly');
    const store = tx.objectStore('mediaBlobs');
    const cursor = store.openCursor();
    const now = Date.now();
    const self = this;
    await this._processCursor(cursor, (c) => {
      const rec = c.value;
      if (rec.isVast && rec.blob && rec.blob.size > 0) {
        const age = now - rec.timestamp;
        if (age < self.vastTTL) {
          const blobUrl = self.createTrackedBlobUrl(rec.blob, rec.url, rec.size);
          self._vastBlobMap.set(rec.url, blobUrl);
        }
      }
    });
    if (this._vastBlobMap.size > 0) {
      console.log(this.logPrefix + ' Rebuilt VAST blob map: ' + this._vastBlobMap.size + ' entries');
    }
  }

  /**
   * Synchronous lookup of a cached VAST blob URL by creativeId.
   * Returns null if no cached blob exists for this creative.
   *
   * @param {string} creativeId - IMA SDK ad ID (e.g. '709996466052')
   * @returns {string|null} blob: URL or null
   */
  getVastBlobUrl(creativeId) {
    if (!creativeId) return null;
    return this._vastBlobMap.get('vast:' + creativeId) || null;
  }

  /**
   * Async fallback for getVastBlobUrl — queries IndexedDB directly when the
   * in-memory map hasn't been rebuilt yet (e.g., first ad after page reload
   * before ensureInitialized() completes). If found, populates the in-memory
   * map so subsequent synchronous lookups work.
   *
   * @param {string} creativeId - IMA SDK ad ID
   * @returns {Promise<string|null>} blob: URL or null
   */
  async getVastBlobUrlAsync(creativeId) {
    if (!creativeId) return null;
    const cacheKey = 'vast:' + creativeId;
    // Check in-memory first
    const existing = this._vastBlobMap.get(cacheKey);
    if (existing) return existing;
    // Fall back to IndexedDB
    try {
      if (!this.db) await this.init();
      if (!this.db) return null;
      const rec = await this._getMediaRecord(cacheKey);
      if (rec && rec.isVast && rec.blob && rec.blob.size > 0) {
        const age = Date.now() - rec.timestamp;
        if (age < this.vastTTL) {
          const blobUrl = this.createTrackedBlobUrl(rec.blob, cacheKey, rec.size);
          this._vastBlobMap.set(cacheKey, blobUrl);
          return blobUrl;
        }
      }
    } catch (e) {
      // Non-critical — fall through to network fetch
    }
    return null;
  }

  /**
   * Get VAST cache statistics for telemetry
   * @returns {Object} VAST-specific cache stats
   */
  async getVastCacheStats() {
    await this.ensureInitialized();

    const stats = {
      vastItems: 0,
      vastSizeBytes: 0,
      deviceClass: this.deviceClass,
      limits: this.getStorageLimitsForDevice()
    };

    try {
      const tx = this.db.transaction('mediaBlobs', 'readonly');
      const store = tx.objectStore('mediaBlobs');
      const cursor = store.openCursor();

      await this._processCursor(cursor, (c) => {
        if (c.value.isVast || c.value.priority === 1.5) {
          stats.vastItems++;
          stats.vastSizeBytes += c.value.size || 0;
        }
      });

      stats.vastSizeFormatted = this._formatBytes(stats.vastSizeBytes);
    } catch (error) {
      console.error(`${this.logPrefix} Failed to get VAST stats:`, error);
    }

    return stats;
  }

  /**
   * Evict by priority levels (for emergency storage cleanup)
   * @param {number[]} priorities - Priority levels to evict (e.g., [3, 2] for default streams then self-promo)
   */
  async clearByPriority(priorities = [3, 2]) {
    await this.ensureInitialized();

    try {
      const items = await this._getAllMediaRecords();
      let freedBytes = 0;

      for (const priority of priorities) {
        const toEvict = items.filter(item => item.priority === priority);

        for (const item of toEvict) {
          await this.removeMediaFromCache(item.url);
          freedBytes += item.size || 0;
          this.metrics.evictions++;
        }
      }

      console.log(`${this.logPrefix} Emergency eviction by priority:`, priorities, `freed ${this._formatBytes(freedBytes)}`);
      return freedBytes;
    } catch (error) {
      console.error(`${this.logPrefix} Priority eviction failed:`, error);
      return 0;
    }
  }
}

// Create singleton instance
const contentCache = new ContentCache();

// Eagerly initialize and rebuild VAST blob map so that synchronous
// getVastBlobUrl() lookups work for the very first ad after page load.
// Without this, _vastBlobMap is empty until the first async cache method
// is called, causing every ad to re-download from the network.
contentCache.ensureInitialized().catch(() => {});

// Export for ES modules
export default contentCache;

// Also expose on window for vanilla JS usage in trillboards-overlay.js
if (typeof window !== 'undefined') {
  window.TrillboardsContentCache = contentCache;
}
